import { Text } from "@/components/ui/text";
import { View } from "@/components/ui/view";
import { Input, InputField } from "@/components/ui/input";
import * as React from 'react';
import { IconM } from '@/components/media/IconsV2';

const DEFAULT_COLON = ':';
const DEFAULT_VALUE_FULL = `00${DEFAULT_COLON}00${DEFAULT_COLON}00`;

export function isNumber(value) {
    const number = Number(value);
    return !isNaN(number) && String(value) === String(number);
}

export function formatTimeItem(value) {
    return `${value || ''}00`.substring(0, 2);
}

export function validateTimeAndCursor(
    value = '',
    defaultValue = '',
    colon = DEFAULT_COLON,
    cursorPosition = 0
) {
    const [oldH, oldM, oldS] = defaultValue.split(colon);

    let newCursorPosition = Number(cursorPosition);
    let [newH, newM, newS] = String(value).split(colon);

    newH = formatTimeItem(newH);
    if (Number(newH[0]) > 2) {
        newH = oldH;
        newCursorPosition -= 1;
    } else if (Number(newH[0]) === 2) {
        if (Number(oldH[0]) === 2 && Number(newH[1]) > 3) {
            newH = `2${oldH[1]}`;
            newCursorPosition -= 2;
        } else if (Number(newH[1]) > 3) {
            newH = '23';
        }
    }

    newM = formatTimeItem(newM);
    if (Number(newM[0]) > 5) {
        newM = oldM;
        newCursorPosition -= 1;
    }

    newS = formatTimeItem(newS);
    if (Number(newS[0]) > 5) {
        newS = oldS;
        newCursorPosition -= 1;
    }

    const validatedValue = `${newH}${colon}${newM}${colon}${newS}`;

    return [validatedValue, newCursorPosition];
}


export default class WebTimerInput extends React.Component {
    static defaultProps = {
        input: null,
        colon: DEFAULT_COLON
    };

    constructor(props) {
        super(props);

        const _defaultValue = DEFAULT_VALUE_FULL;
        const _colon = props.colon && props.colon.length === 1 ? props.colon : DEFAULT_COLON;
        const [validatedTime] = validateTimeAndCursor(this.props.value, _defaultValue, _colon);

        this.state = {
            value: validatedTime,
            _colon,
            _defaultValue,
            _maxLength: _defaultValue.length
        };
        this.onInputChange = this.onInputChange.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (this.props.value !== prevProps.value) {
            const [validatedTime] = validateTimeAndCursor(
                this.props.value,
                this.state._defaultValue,
                this.state._colon
            );
            if (!this.props.disabled) {
                this.setState({
                    value: validatedTime
                });
            }
        }
    }

    onInputChange(event) {
        const oldValue = this.state.value;
        const inputEl = event.target;
        const inputValue = inputEl.value;
        const position = inputEl.selectionEnd || 0;
        const isTyped = inputValue.length > oldValue.length;
        const cursorCharacter = inputValue[position - 1];
        const addedCharacter = isTyped ? cursorCharacter : null;
        const removedCharacter = isTyped ? null : oldValue[position];
        const replacedSingleCharacter = inputValue.length === oldValue.length ? oldValue[position - 1] : null;
        const colon = this.state._colon;

        let newValue = oldValue;
        let newPosition = position;

        if (addedCharacter !== null) {
            if (position > this.state._maxLength) {
                newPosition = this.state._maxLength;
            } else if ((position === 3 || position === 6) && addedCharacter === colon) {
                newValue = `${inputValue.substring(0, position - 1)}${colon}${inputValue.substring(position + 1)}`;
            } else if ((position === 3 || position === 6) && isNumber(addedCharacter)) {
                newValue = `${inputValue.substring(0, position - 1)}${colon}${addedCharacter}${inputValue.substring(position + 2)}`;
                newPosition = position + 1;
            } else if (isNumber(addedCharacter)) {
                // user typed a number
                newValue = inputValue.substring(0, position - 1) + addedCharacter + inputValue.substring(position + 1);
                if (position === 2 || position === 5) {
                    newPosition = position + 1;
                }
            } else {
                // if user typed NOT a number, then keep old value & position
                newPosition = position - 1;
            }
        } else if (replacedSingleCharacter !== null) {
            // user replaced only a single character
            if (isNumber(cursorCharacter)) {
                if (position - 1 === 2 || position - 1 === 5) {
                    newValue = `${inputValue.substring(0, position - 1)}${colon}${inputValue.substring(position)}`;
                } else {
                    newValue = inputValue;
                }
            } else {
                // user replaced a number on some non-number character
                newValue = oldValue;
                newPosition = position - 1;
            }
        } else if (typeof cursorCharacter !== 'undefined' && cursorCharacter !== colon && !isNumber(cursorCharacter)) {
            // set of characters replaced by non-number
            newValue = oldValue;
            newPosition = position - 1;
        } else if (removedCharacter !== null) {
            if ((position === 2 || position === 5) && removedCharacter === colon) {
                newValue = `${inputValue.substring(0, position - 1)}0${colon}${inputValue.substring(position)}`;
                newPosition = position - 1;
            } else {
                // user removed a number
                newValue = `${inputValue.substring(0, position)}0${inputValue.substring(position)}`;
            }
        }

        const [validatedTime, validatedCursorPosition] = validateTimeAndCursor(
            newValue,
            oldValue,
            colon,
            newPosition
        );
        if (!this.props.disabled) {
            this.setState({ value: validatedTime }, () => {
                inputEl.selectionStart = validatedCursorPosition;
                inputEl.selectionEnd = validatedCursorPosition;
            });
        }

        this.props.setTime(validatedTime)

        event.persist();
    }

    render() {
        const { value } = this.state;
        const onChangeHandler = (event) =>
            this.onInputChange(event);

        return (this.props.disabled ? <View className="border-b-primary-50">
            <Text bold className="self-center text-2xl text-secondary-900">
                {value || '00:00:00'}
            </Text>
        </View> : <Input variant='underlined' className="self-center border-b-primary-50">
            <InputField placeholder='00:00:00' value={value} inputMode='numeric' selectionColor={"#D2D4D7"} onChange={onChangeHandler} className="text-3xl font-bold w-[140px] text-secondary-900 bg-transparent my-1" />
            {!this.props.disabled ?
                <IconM className='' size={24} onPress={() => { console.log('clear'); this.setState({ value: '00:00:00' }); this.props.setTime('00:00:00') }} name='clear' />
                : <></>}
        </Input>);
    }
}
